import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Offer } from '../../modules/rate-quote/types';
import { defaultLockModalState, LockData } from './state';

export const lockModalSlice = createSlice({
  name: 'lockModal',
  initialState: defaultLockModalState,
  reducers: {
    closeLockModal: (state) => ({
      ...state,
      isOpen: false,
      offer: null,
      modalLock: null,
      networkError: null,
      link: null
    }),
    lockOfferRequest: (
      state,
      _: PayloadAction<{
        rateQuoteId: string;
        borrowerScenarioId: string;
        sourceId: string;
        offer: Offer;
        offerId: string;
        rgTitle: boolean | undefined;
        loanId: string;
        attorney?: string;
        notes: string;
        userInfo: { email: string; name: string } | null;
        float: boolean;
      }>
    ) => ({
      ...state,
      loading: true,
      isOpen: true,
      networkError: null
    }),
    lockOfferSuccess: (
      state,
      { payload }: PayloadAction<{ success: boolean; pending: boolean }>
    ) => ({
      ...state,
      loading: payload.pending,
      isOpen: true,
      networkError: null
    }),
    lockOfferFailure: (state, { payload }: PayloadAction<string>) => ({
      ...state,
      loading: false,
      networkError: payload
    }),
    getLockRequest: (
      state,
      { payload: { offer } }: PayloadAction<{ rateQuoteId: string; offer: Offer }>
    ) => ({
      ...state,
      offer: {
        offerId: offer.offerId,
        product: offer.attributes.product,
        lender: offer.lender,
        rate: offer.productDetails.rate,
        locked: offer.locked,
        fees: offer.fees.filter(({ id }) => id === 'lenderCredit' || id === 'points')
      },
      loading: true,
      isOpen: true,
      networkError: null
    }),
    getLockSuccess: (
      state,
      {
        payload
      }: PayloadAction<{
        offerId: string;
        loanId: string;
        pending: boolean;
        rgTitle: boolean | undefined;
        attorney?: string;
        notes: string;
        link: string;
        lockId: string;
        updatedHubspot?: boolean;
        float: boolean;
      }>
    ) => ({
      ...state,
      modalLock: {
        ...state.modalLock!,
        loanId: payload.loanId,
        rgTitle: payload.rgTitle,
        attorney: payload.rgTitle ? payload.attorney : undefined,
        provider: payload.rgTitle ? undefined : payload.attorney,
        notes: payload.notes || '',
        lockId: payload.lockId,
        float: payload.float
      },
      loading: payload.pending,
      link: payload.link,
      isOpen: true,
      networkError: !payload.pending && !payload.link ? 'pdf' : null,
      updatedHubspot: payload.updatedHubspot || false
    }),
    getLockFailure: (state, { payload }: PayloadAction<string>) => ({
      ...state,
      loading: false,
      networkError: payload
    }),
    deleteLockRequest: (
      state,
      _: PayloadAction<{
        rateQuoteId: string;
        offerId: string;
        lockId: string;
        sourceId: string;
      }>
    ) => ({
      ...state,
      loading: true,
      networkError: null,
      isOpen: true
    }),
    deleteLockSuccess: (state) => ({
      ...state,
      loading: false,
      networkError: null,
      link: null
    }),
    deleteLockFailure: (state, { payload }: PayloadAction<string>) => ({
      ...state,
      loading: false,
      networkError: payload
    }),
    editLockRequest: (
      state,
      _: PayloadAction<{
        rateQuoteId: string;
        borrowerScenarioId: string;
        sourceId: string;
        offerId: string;
        offer: Offer;
        rgTitle: boolean | undefined;
        loanId: string;
        attorney?: string;
        notes: string;
        lockId: string;
        userInfo: { email: string; name: string } | null;
        link: string;
        float: boolean;
      }>
    ) => ({
      ...state,
      loading: true,
      isOpen: true,
      networkError: null
    }),
    updateLockModalFields: (
      state,
      { payload }: PayloadAction<{ id: string; value: string | boolean }>
    ) => ({
      ...state,
      modalLock: {
        ...state.modalLock!,
        [payload.id]: payload.value
      }
    }),
    seedLockModal: (
      state,
      { payload: { offer, modalLock } }: PayloadAction<{ modalLock: LockData; offer: Offer }>
    ) => ({
      ...state,
      modalLock,
      offer: {
        offerId: offer.offerId,
        product: offer.attributes.product,
        lender: offer.lender,
        rate: offer.productDetails.rate,
        locked: offer.locked,
        fees: offer.fees.filter(({ id }) => id === 'lenderCredit' || id === 'points')
      },
      isOpen: true,
      networkError: null
    })
  }
});

export const {
  closeLockModal,
  lockOfferRequest,
  lockOfferSuccess,
  lockOfferFailure,
  getLockRequest,
  getLockSuccess,
  getLockFailure,
  deleteLockRequest,
  deleteLockSuccess,
  deleteLockFailure,
  editLockRequest,
  updateLockModalFields,
  seedLockModal
} = lockModalSlice.actions;

export const reducer = lockModalSlice.reducer;
